import React, { useState, useRef } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  {
    key: 'deals',
    title: 'Deals',
    endpoint: '/import/deals',
    columns: 'Deal ID, Shortcode, Deal Name, Funding Date, Advance Amount, Factor Rate, Payback, Payment Amount, Term Days, Status, Fees, Refi From Deal IDs, Refi To Deal ID, Refi Group ID',
    description: 'Import deals from CSV. Skips deals that already exist by Deal ID.',
  },
  {
    key: 'transactions',
    title: 'Transactions',
    endpoint: '/import/transactions',
    columns: 'Transaction ID, Deal ID, Deal Name, Transaction Date, Amount, Transaction Type, Notes/Description',
    description: 'Import transactions from CSV. Skips duplicates by Transaction ID.',
  },
  {
    key: 'syndications',
    title: 'Syndications',
    endpoint: '/import/syndications',
    columns: 'Syndication ID, Deal ID, Deal Name, Syndicator ID, Syndicator Name, Percent Invested, Advance Amount, RTR Amount, Role, Collect Fees, Running Collection, Total Withdrawals, Balance',
    description: 'Import syndication records from CSV. Skips duplicates by Syndication ID.',
  },
];

function ImportSection({ section }) {
  const { getAuthHeaders } = useAuth();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setResult(null);
    setError(null);
    setUploading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const resp = await axios.post(`${API}${section.endpoint}`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setResult(resp.data);
      if (resp.data.inserted > 0) {
        toast.success(`${resp.data.inserted} ${section.key} imported`);
      } else {
        toast.info('No new records imported (all skipped or rejected)');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Upload failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Card className="border-border/60" data-testid={`import-section-${section.key}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-muted-foreground" />
            {section.title}
          </CardTitle>
          <div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              className="hidden"
              data-testid={`file-input-${section.key}`}
              onChange={handleUpload}
            />
            <Button
              size="sm"
              disabled={uploading}
              data-testid={`upload-btn-${section.key}`}
              onClick={() => fileRef.current?.click()}
            >
              {uploading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importing...</>
              ) : (
                <><Upload className="h-4 w-4 mr-2" /> Upload CSV</>
              )}
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-1">{section.description}</p>
      </CardHeader>
      <CardContent className="pt-0">
        <details className="text-xs text-muted-foreground mb-3">
          <summary className="cursor-pointer hover:text-foreground">Expected columns</summary>
          <p className="mt-1 font-mono bg-muted/50 rounded p-2 break-all">{section.columns}</p>
        </details>

        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-950/40 border border-red-800 mb-3" data-testid={`error-${section.key}`}>
            <XCircle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
            <p className="text-sm text-red-300">{error}</p>
          </div>
        )}

        {result && (
          <div className="space-y-3" data-testid={`result-${section.key}`}>
            <div className="flex items-start gap-2 p-3 rounded-lg bg-green-950/40 border border-green-800">
              <CheckCircle2 className="h-4 w-4 text-green-400 mt-0.5 shrink-0" />
              <span className="text-sm text-green-300">{result.total_rows} rows processed</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <StatBox label="Inserted" value={result.inserted} color="text-green-400" testId={`inserted-${section.key}`} />
              <StatBox label="Skipped" value={result.skipped} color="text-yellow-400" testId={`skipped-${section.key}`} />
              <StatBox label="Rejected" value={result.rejected} color="text-red-400" testId={`rejected-${section.key}`} />
            </div>
            {result.rejected_rows?.length > 0 && (
              <details className="text-sm">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  <Badge variant="outline" className="ml-1">{result.rejected}</Badge> rejected row(s)
                </summary>
                <ul className="mt-2 space-y-1 pl-4 text-muted-foreground text-xs max-h-40 overflow-y-auto">
                  {result.rejected_rows.map((r, i) => (
                    <li key={i} className="flex items-start gap-1">
                      <AlertCircle className="h-3 w-3 text-red-400 mt-0.5 shrink-0" />
                      Row {r.row}: {r.reason}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatBox({ label, value, color, testId }) {
  return (
    <div className="p-3 rounded-lg bg-muted/50 text-center">
      <p className={`text-2xl font-bold ${color}`} data-testid={testId}>{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

export default function BulkImport() {
  return (
    <Layout>
      <div className="space-y-6 px-4 sm:px-0" data-testid="bulk-import-page">
        <h1 className="text-2xl font-bold">Bulk CSV Import</h1>
        <p className="text-muted-foreground text-sm">
          Upload CSV files to import deals, transactions, and syndications in bulk. Existing records are skipped automatically.
        </p>
        <div className="grid gap-6">
          {SECTIONS.map((s) => (
            <ImportSection key={s.key} section={s} />
          ))}
        </div>
      </div>
    </Layout>
  );
}
